/* import __COLOCATED_TEMPLATE__ from './unprotected-jwt-attributes.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import ajax from 'embercom/lib/ajax';

interface Signature {
  Args: {};
}

export default class UnprotectedJwtAttributes extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;

  @tracked attributes: Array<{ attribute_name: string; action: string }> = [];
  @tracked isLoading = false;
  @tracked errorMessage: string | null = null;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    this.fetchAttributes();
  }

  get app() {
    return this.appService.app;
  }

  get appId() {
    return this.app.id;
  }

  get columns() {
    return [
      {
        label: this.intl.t('messenger.security-settings.unprotected-jwt-attributes.attribute_name'),
        valuePath: 'attribute_name',
      },
      {
        label: this.intl.t(
          'messenger.security-settings.unprotected-jwt-attributes.manage_attribute',
        ),
        valuePath: 'manage_attribute',
      },
    ];
  }

  @action
  linkToManageAttribute(attributeName: string) {
    return this.router.urlFor('apps.app.settings.data.people', {
      queryParams: { tab: 'attributes', attribute_name: attributeName },
    });
  }

  @action
  async fetchAttributes(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.attributes = [];

    try {
      let response = await ajax({
        url: '/ember/ping_domain_errors/unprotected_attributes_in_jwts',
        type: 'GET',
        data: {
          app_id: this.appId,
        },
      });
      response.attributes.forEach((attribute: string) => {
        this.attributes.push({ attribute_name: attribute, action: 'manage' });
      });
    } catch (error) {
      console.error('Error fetching unprotected JWT attributes:', error);
      this.errorMessage = this.intl.t(
        'apps.app.settings.installation.installation.new.web.setup-idv.unprotected-jwt-attributes.error',
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  trackManageAttributeClick(attributeName: string): void {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'manage_attribute_link',
      attribute_name: attributeName,
      place: 'security_settings',
      section: 'unprotected_jwt_attributes',
    });
  }

  get hasAttributes() {
    return this.attributes.length > 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'InstallationNew::Web::IdvSetup::UnprotectedJwtAttributes': typeof UnprotectedJwtAttributes;
    'installation-new/web/idv-setup/unprotected-jwt-attributes': typeof UnprotectedJwtAttributes;
  }
}
